import { ChangeDetectionStrategy, Component, OnInit, inject } from "@angular/core";
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from "@angular/material/bottom-sheet";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent, MatDialogActions } from "@angular/material/dialog";
import { DocumentsPlusConfigurationService } from "@app/app-modules/documents-plus/documents-plus-configuration/documents-plus-configuration.service";
import { InBasketAttach, InBasketDocumentTypes } from "@app/app-modules/documents-plus/documents-plus-in-basket/documents-plus-in-basket.model";
import { DocumentsPlusInBasketService } from "@app/app-modules/documents-plus/documents-plus-in-basket/documents-plus-in-basket.service";
import { DocumentsPlusAttachParms } from "@app/app-modules/documents-plus/documents-plus.model";
import { BaseFormGroupComponent } from "@app/shared/components/abstracts/base-component";
import { DPLUS_URL } from "@app/shared/constants/url.constants";
import { forkJoin, switchMap, tap } from "rxjs";
import { FunctionPipe } from "@app/shared/pipes/function.pipe";
import { TranslocoPipe } from "@jsverse/transloco";
import { ClickStopDirective } from "@app/shared/directives/click-stop.directive";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatOptionModule } from "@angular/material/core";
import { MatSelectModule } from "@angular/material/select";
import { AutoFocusDirective } from "@app/shared/directives/auto-focus.directive";
import { FormFieldTrimDirective } from "@app/shared/directives/form-field-trim.directive";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { CdkDrag, CdkDragHandle } from "@angular/cdk/drag-drop";
import { AsyncPipe } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FileDownloadService } from "@app/core/services/file-download.service";
import { DocumentsPlusScanning } from "@app/app-modules/documents-plus/documents-plus-scanning/documents-plus-scanning.model";
import { TypeAheadComponent } from "@app/shared/components/type-ahead/type-ahead/type-ahead.component";
import { DocumentsPlusService } from "@app/app-modules/documents-plus/documents-plus.service";
import { Applications } from "@admin/application-settings/applications/applications.model";
import { ApplicationsService } from "@admin/application-settings/applications/applications.service";

@Component({
  selector: "app-documents-plus-in-basket-attach",
  templateUrl: "./documents-plus-in-basket-attach.component.html",
  styleUrls: ["./documents-plus-in-basket-attach.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    CdkDrag,
    CdkDragHandle,
    FormsModule,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormFieldModule,
    MatInputModule,
    FormFieldTrimDirective,
    AutoFocusDirective,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatDialogActions,
    MatButtonModule,
    ClickStopDirective,
    TranslocoPipe,
    FunctionPipe,
    AsyncPipe,
    TypeAheadComponent,
    MatIconModule,
    MatTooltipModule
  ]
})
export class DocumentsPlusInBasketAttachComponent extends BaseFormGroupComponent implements OnInit {
  public dialogRef = inject<MatDialogRef<DocumentsPlusInBasketAttachComponent>>(MatDialogRef, { optional: true });
  public sheetRef = inject<MatBottomSheetRef<DocumentsPlusInBasketAttachComponent>>(MatBottomSheetRef, {
    optional: true
  });
  private dialogData = inject<DocumentsPlusAttachParms>(MAT_DIALOG_DATA, { optional: true });
  private sheetData = inject<DocumentsPlusAttachParms>(MAT_BOTTOM_SHEET_DATA, { optional: true });
  private inBasketService = inject(DocumentsPlusInBasketService);
  private documentsPlusService = inject(DocumentsPlusService);
  private appService = inject(ApplicationsService);

  dplusConfigService = inject(DocumentsPlusConfigurationService);
  fds = inject(FileDownloadService);

  tab: undefined;
  dataContext: InBasketAttach;
  documentTypes: InBasketDocumentTypes[] = [];
  selectedData: DocumentsPlusAttachParms;
  uploadModel: DocumentsPlusScanning;
  applications: Applications[] = [];
  applicationId: string;

  ngOnInit(): void {
    this.selectedData = this.dialogData ?? this.sheetData;
    this.tabService.activeMenuOptionApplication = this.selectedData.applicationId;
    this.onInitCreateForm();
  }

  onInitCreateForm() {
    forkJoin({
      model: this.getDataModel(DPLUS_URL.INBASKET_ATTACH),
      uploadModel: this.getDataModel(DPLUS_URL.INBASKET_SCANNING),
      docTypes: this.getDocumentTypes(),
      apps: this.getApplications()
    })
      .pipe(
        tap((resp) => {
          this.dataModel = resp.model;
          this.uploadModel = resp.uploadModel.model;
          this.dataContext = this.dataModel.model;
          this.documentTypes = resp.docTypes;
          this.applications = resp.apps;
          this.onInitFormGroupSetup();
        }),
        switchMap(() => {
          return forkJoin({ idList: this.getIdList(), config: this.getConfig() });
        })
      )
      .subscribe(() => {
        this.setCompanyId();
        this.setTabApplicationId();

        this.dataContext.index.value = this.selectedData?.index ?? "";
        this.dataContext.descriptionToUse = this.dplusConfigService?.appConfig()?.descriptionToUse;
        this.setFormControlValues();

        if (this.selectedData?.disabled) {
          this.formGroup.get("companyNumber").disable();
          this.formGroup.get("index").disable();
        }
        // This field will always be read only.
        this.formGroup.get("documentTypeDescription").disable();
      })
      .add(() => {
        if (this.selectedData?.autoAttach) {
          this.save();
        } else {
          this.showForm.set(true);
        }
      });
  }

  setCompanyId() {
    const defaultId = this.dplusConfigService.dplusIds().find((x) => x.default)?.id;
    this.dataContext.companyNumber.value = this.selectedData?.company ?? defaultId ?? this.dplusConfigService.dplusIds()[0]?.id;
  }

  /**
   * Set copied text from viewer to first available input field.
   * @returns
   */
  setSelectedText() {
    if (!this.selectedData?.selectedText) {
      return;
    }

    if (!this.dplusConfigService.appConfig().isPositionToIndex && !this.dplusConfigService.dplusIds()?.length) {
      this.dataContext.companyNumber.value = this.selectedData?.selectedText;
    } else {
      this.dataContext.index.value = this.selectedData?.selectedText;
    }
  }

  getConfig() {
    return this.dplusConfigService.fetchConfigurationByAppId(this.appControl.value);
  }

  getIdList() {
    return this.dplusConfigService.fetchConfigurationIdList(this.appControl.value);
  }

  /**
   * Retrieve doc types
   * @returns
   */
  getDocumentTypes() {
    return this.inBasketService.fetchDocumentTypes();
  }

  getApplications() {
    return this.appService.fetchFilteredApplications("M");
  }

  refreshApplicationValues() {
    forkJoin({ idList: this.getIdList(), config: this.getConfig() }).subscribe({
      next: (resp) => {
        if (resp) {
          this.setCompanyId();
        }
      },
      error: () => {}
    });
  }

  /**
   * onInitFormGroupSetup()
   *
   * Create form group controls and setup and observers
   */
  onInitFormGroupSetup() {
    const controls = {
      applicationId: this.formBuilder.control(
        this.selectedData?.applicationId ?? this.applications[0].applicationId,
        !this.selectedData?.applicationId ? Validators.required : undefined
      ),
      documentType: this.formBuilder.control("", this.showDocumentType ? Validators.required : undefined),
      documentTypeDescription: this.formBuilder.control(`${this.selectedData?.documentTypeDescription} (${this.selectedData?.documentType})`),
      deleteDoc: this.formBuilder.control(this.canAutoDelete)
    };
    this.formGroup = this.formService.formGroupBuilder(this.dataModel.fieldRules, controls);
  }

  /**
   * setDataModelValues()
   *
   * Set data model values from form control values
   */
  setDataModelValues() {
    this.setDataModel(this.formGroup, this.dataContext);
  }

  /**
   * setFormControl()
   *
   * Set data model values to form control values
   */
  setFormControlValues() {
    this.setFormControl(this.formGroup, this.dataContext);
    this.validateAllFormFields(this.formGroup);
  }

  doCreateData() {
    this.formGroup.disable();

    if (this.selectedData?.reportsPlus) {
      this.doCreateInbasketAndAttach();
    } else {
      this.doAttach();
    }
  }

  /**
   * Create InBasket item and auto attach
   */
  doCreateInbasketAndAttach() {
    this.uploadModel.applicationId = this.appControl.value;
    this.uploadModel.inbasketDocument = this.selectedData.ids[0];
    this.uploadModel.fileType = this.fds.getDocumentType(this.uploadModel.inbasketDocument);
    this.uploadModel.description.value = this.selectedData.documentTypeDescription;
    this.uploadModel.resolution.value = 1;
    this.uploadModel.documentType = this.formGroup.get("documentType").value;
    this.uploadModel.userId.value = this.authService.credentials.username;

    this.documentsPlusService.addDocument(this.uploadModel).subscribe({
      next: (r) => {
        this.selectedData.ids = [r.uniqueId];
        this.doAttach();
      },
      error: () => {}
    });
  }

  /**
   * Attach Existing InBasket item
   */
  doAttach() {
    this.dataContext.documents = this.selectedData.ids;
    this.inBasketService
      .attachDocument(this.dataContext)
      .subscribe({
        next: () => {
          const msg = this.i18nService.translate("DOCUMENTSPLUS.MSG_ATTACH");
          this.notifyService.success("", msg);
          this.close(this.formGroup.get("deleteDoc").value);
        },
        error: (error) => {
          this.dataContext = error.error.data[0];
          this.setFormControlValues();
        }
      })
      .add(() => {
        this.formGroup.enable();
      });
  }

  /**
   * Attach doc
   */
  save() {
    this.validateAllFormFields(this.formGroup);
    if (this.formGroup.valid || this.selectedData?.isWebFace) {
      this.setDataModelValues();
      this.doCreateData();
    }
  }

  /**
   * Close the modal/sheet and return if he inbasket doc should be deleted
   * @param deleteDoc
   */
  close(deleteDoc?: boolean, closeOnly?: boolean) {
    let data = { deleteDoc: deleteDoc };
    if (closeOnly) {
      data = null;
    }

    this.dialogRef ? this.dialogRef.close(data) : this.sheetRef.dismiss(data);
  }

  idLabel = () => {
    return this.i18nService.translate(this.dplusConfigService?.appConfig()?.idFieldText || "DOCUMENTSPLUS.LBL_ID");
  };

  indexLabel = () => {
    return this.i18nService.translate(this.dplusConfigService?.appConfig()?.indexFieldText || "DOCUMENTSPLUS.LBL_INDEX");
  };

  /**
   * Set description to document type
   */
  onDocumentTypeChange(description: string) {
    const control = this.formGroup.get("documentType");
    if (this.dplusConfigService?.descrAutoPopulate && !control?.pristine) {
      this.selectedData.documentTypeDescription = description;
    }
  }

  setTabApplicationId() {
    this.tabService.activeMenuOptionApplication = this.appControl.value;
  }

  /**
   * Return if the user can delete a document
   */
  get canDelete(): boolean {
    return this.userSettingsService.inbasketSettings.canDeleteInBasketItems;
  }

  /**
   * Return if user will auto-delete document
   */
  get canAutoDelete(): boolean {
    return this.userSettingsService.inbasketSettings.autoDeleteInBasketItems;
  }

  get showDocumentType() {
    return this.selectedData?.reportsPlus && !this.selectedData.documentType;
  }

  get appControl() {
    return this.formGroup.get("applicationId");
  }
}
